import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["as", "children", "className"],
  _excluded2 = ["align", "as", "autoAlign", "className", "children", "defaultOpen"],
  _excluded3 = ["children", "className", "label", "as"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import cx from 'classnames';
import React, { useContext, useRef, useState } from 'react';
import { PopoverContent, Popover } from '../Popover/index.js';
import { Escape } from '../../internal/keyboard/keys.js';
import { match } from '../../internal/keyboard/match.js';
import { useWindowEvent } from '../../internal/useEvent.js';
import { useId } from '../../internal/useId.js';
import { usePrefix } from '../../internal/usePrefix.js';

/**
 * Used to render the label for a Toggletip
 */
function ToggletipLabel(_ref) {
  var _ref$as = _ref.as,
    BaseComponent = _ref$as === void 0 ? 'span' : _ref$as,
    children = _ref.children,
    customClassName = _ref.className,
    rest = _objectWithoutProperties(_ref, _excluded);
  var prefix = usePrefix();
  var className = cx("".concat(prefix, "--toggletip-label"), customClassName);
  var BaseComponentAsAny = BaseComponent;
  return /*#__PURE__*/React.createElement(BaseComponentAsAny, _extends({
    className: className
  }, rest), children);
}
// Used to coordinate accessibility props between button and content along with
// the actions to open and close the toggletip
var ToggletipContext = /*#__PURE__*/React.createContext(undefined);
function useToggletip() {
  return useContext(ToggletipContext);
}
/**
 * Used as a container for the button and content of a toggletip. This component
 * is responsible for coordinating between interactions with the button and the
 * visibility of the content
 */
function Toggletip(_ref2) {
  var _cx;
  var align = _ref2.align,
    as = _ref2.as,
    autoAlign = _ref2.autoAlign,
    customClassName = _ref2.className,
    children = _ref2.children,
    _ref2$defaultOpen = _ref2.defaultOpen,
    defaultOpen = _ref2$defaultOpen === void 0 ? false : _ref2$defaultOpen,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  var ref = useRef(null);
  var _useState = useState(defaultOpen),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var prefix = usePrefix();
  var id = useId();
  var className = cx("".concat(prefix, "--toggletip"), customClassName, (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--toggletip--open"), open), _defineProperty(_cx, "".concat(prefix, "--autoalign"), autoAlign), _cx));
  var actions = {
    toggle: function toggle() {
      setOpen(!open);
    },
    close: function close() {
      setOpen(false);
    }
  };
  var value = {
    buttonProps: {
      'aria-expanded': open,
      'aria-controls': id,
      'aria-describedby': open ? id : undefined,
      onClick: actions.toggle
    },
    contentProps: {
      id: id
    },
    onClick: {
      onClick: actions.toggle
    }
  };
  var onKeyDown = function onKeyDown(event) {
    if (open && match(event, Escape)) {
      var _ref$current;
      actions.close();

      // If the menu is closed while focus is still inside the menu, it should return to the trigger button  (#12922)
      var button = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.children[0];
      if (button instanceof HTMLButtonElement) {
        button.focus();
      }
    }
  };
  var handleBlur = function handleBlur(event) {
    // Do not close if the menu itself is clicked, should only close on focus out
    if (open && event.relatedTarget === null) {
      return;
    }
    if (!event.currentTarget.contains(event.relatedTarget)) {
      // The menu should be closed when focus leaves the `Toggletip`  (#12922)
      actions.close();
    }
  };

  // If the `Toggletip` is the last focusable item in the tab order, it should also close when the browser window loses focus  (#12922)
  useWindowEvent('blur', function () {
    if (open) {
      actions.close();
    }
  });
  useWindowEvent('click', function (_ref3) {
    var _ref$current2;
    var target = _ref3.target;
    if (open && target instanceof Node && !((_ref$current2 = ref.current) !== null && _ref$current2 !== void 0 && _ref$current2.contains(target))) {
      actions.close();
    }
  });
  return /*#__PURE__*/React.createElement(ToggletipContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(Popover, _extends({
    align: align,
    as: as,
    caret: true,
    className: className,
    dropShadow: false,
    highContrast: true,
    open: open,
    onKeyDown: onKeyDown,
    onBlur: handleBlur,
    ref: ref,
    autoAlign: autoAlign
  }, rest), children));
}
/**
 * `ToggletipButton` controls the visibility of the Toggletip through mouse
 * clicks and keyboard interactions.
 */

var ToggletipButton = /*#__PURE__*/React.forwardRef(function ToggletipButton(_ref4, ref) {
  var children = _ref4.children,
    customClassName = _ref4.className,
    _ref4$label = _ref4.label,
    label = _ref4$label === void 0 ? 'Show information' : _ref4$label,
    BaseComponent = _ref4.as,
    rest = _objectWithoutProperties(_ref4, _excluded3);
  var toggletip = useToggletip();
  var prefix = usePrefix();
  var className = cx("".concat(prefix, "--toggletip-button"), customClassName);
  var ComponentToggle = BaseComponent !== null && BaseComponent !== void 0 ? BaseComponent : 'button';
  if (ComponentToggle !== 'button') {
    return /*#__PURE__*/React.createElement(ComponentToggle, _extends({}, toggletip === null || toggletip === void 0 ? void 0 : toggletip.onClick, {
      className: className
    }, rest), children);
  }
  return /*#__PURE__*/React.createElement("button", _extends({}, toggletip === null || toggletip === void 0 ? void 0 : toggletip.buttonProps, {
    "aria-label": label,
    type: "button",
    className: className,
    ref: ref
  }, rest), children);
});
ToggletipButton.displayName = 'ToggletipButton';
/**
 * `ToggletipContent` is a wrapper around `PopoverContent`. It places the
 * `children` passed in as a prop inside of `PopoverContent` so that they will
 * be rendered inside of the popover for this component.
 */
var ToggletipContent = /*#__PURE__*/React.forwardRef(function ToggletipContent(_ref5, ref) {
  var children = _ref5.children,
    customClassName = _ref5.className;
  var toggletip = useToggletip();
  var prefix = usePrefix();
  return /*#__PURE__*/React.createElement(PopoverContent, _extends({
    className: customClassName
  }, toggletip === null || toggletip === void 0 ? void 0 : toggletip.contentProps, {
    ref: ref
  }), /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--toggletip-content")
  }, children));
});
ToggletipContent.displayName = 'ToggletipContent';
/**
 * `ToggletipActions` is a container for one or two actions present at the base
 * of a toggletip. It is used for layout of these items.
 */
function ToggletipActions(_ref6) {
  var children = _ref6.children,
    customClassName = _ref6.className;
  var prefix = usePrefix();
  var className = cx("".concat(prefix, "--toggletip-actions"), customClassName);
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, children);
}
export { Toggletip, ToggletipActions, ToggletipButton, ToggletipContent, ToggletipLabel };